<script lang="ts">
	/**
	 * Everything one customer has ever agreed to, grouped by the purchase it was agreed for (§4.7).
	 *
	 * **An acceptance only means anything against the thing that was bought**, which is why the
	 * per-purchase panels on the sale screens exist and why there is deliberately no flat
	 * account-wide list anywhere in the app: the same term accepted twice is two different pieces of
	 * evidence, and a list that ran them together would answer "did they agree to X" for a customer
	 * who has bought once and for nobody else. What was missing is the other half of the question —
	 * *has this customer ever accepted X, and against what* — which support could previously only
	 * answer by opening every subscription and every order in turn.
	 *
	 * So the grouping is the feature, and it is the catalogue's: one table, a heading row per group,
	 * every row on one set of columns. A table per purchase would put each group's terms on its own
	 * private grid, and the eye could no longer compare a version or a date down the page.
	 *
	 * Three rules it shares with `components/AcceptedTerms.svelte`, through `Utilities` rather than
	 * by being a second implementation of them: the body shown is **the snapshot on the acceptance,
	 * never the live term**; the question and its answer render together or not at all; and "edited
	 * since" is a comparison, not a fault. The layouts differ on purpose — one is a purchase's own
	 * evidence, this is a customer's history — but what an acceptance *says* may not.
	 */

	import {
		Badge,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { EmptyState, Panel, RoutedLink } from '../../../components';
	import {
		ACCEPTANCE_VERSION_COPY,
		acceptanceAnswer,
		acceptanceVersionNote,
		formatCalendarDate,
		formatFriendlyDate
	} from '../../../Utilities';
	import type { CustomerAcceptance } from '../../../AppTypes';

	/**
	 * Undefined and empty are different answers and the copy says so.
	 *
	 * Undefined is a server that predates the field, where "this customer has agreed to nothing"
	 * would be a claim the screen cannot support; empty is a customer who genuinely has.
	 */
	export let acceptances: CustomerAcceptance[] | undefined = undefined;

	/** Term, version, answer, accepted, and the disclosure — kept in step with the heading row. */
	const COLUMN_COUNT = 5;

	/** One purchase's worth of terms, or the ones belonging to no purchase we can still name. */
	interface AcceptanceGroup {
		/** The purchase uuid, or `''` for the remainder. Also the `{#each}` key. */
		key: string;
		/** Where the purchase's own screen is, or null for the remainder. */
		href: string | null;
		name: string;
		purchasedAt: string | null;
		acceptances: CustomerAcceptance[];
	}

	/**
	 * The acceptances arranged by purchase.
	 *
	 * Built from the **acceptances**, exactly as the catalogue's groups are built from the products:
	 * a purchase that required no terms belongs on the panels above this one, and an empty heading
	 * here would read as a rendering fault rather than as an absence.
	 *
	 * Ordering is the server's — newest purchase first, and oldest term first inside it, which is
	 * the order the buyer was shown them — so a `Map` is enough and nothing re-sorts. The one thing
	 * decided here is that the **remainder goes last**: an acceptance whose checkout session is gone
	 * is the exception, and a heading for it above the real purchases would read as this customer's
	 * first and most important group.
	 */
	function fnGroup(rows: CustomerAcceptance[]): AcceptanceGroup[] {
		const groups = new Map<string, AcceptanceGroup>();

		for (const acceptance of rows) {
			const key = acceptance.subscriptionUUID ?? acceptance.orderUUID ?? '';
			let group = groups.get(key);
			if (group === undefined) {
				group = {
					key,
					href:
						acceptance.subscriptionUUID !== null
							? `/Settings/Admin/Transactions/Subscription/${acceptance.subscriptionUUID}`
							: acceptance.orderUUID !== null
								? `/Settings/Admin/Transactions/Order/${acceptance.orderUUID}`
								: null,
					/*
					 * The product, or what the group is instead. A purchase whose product row has
					 * since been deleted still has a screen of its own, so it is named by what it
					 * is rather than dropped — a link with no words is not a link anybody clicks.
					 */
					name:
						acceptance.productName ??
						(key === ''
							? 'Not tied to a purchase'
							: acceptance.subscriptionUUID !== null
								? 'Subscription'
								: 'One-time purchase'),
					purchasedAt: acceptance.purchasedAt,
					acceptances: []
				};
				groups.set(key, group);
			}
			group.acceptances.push(acceptance);
		}

		return [...groups.values()].sort((left, right) => {
			if (left.key === '') return 1;
			if (right.key === '') return -1;
			return 0;
		});
	}

	$: groups = acceptances === undefined ? [] : fnGroup(acceptances);

	/** Which body is open. One at a time: two legal texts side by side is neither of them read. */
	let openUUID: string = '';

	function fnToggle(uuid: string): void {
		openUUID = openUUID === uuid ? '' : uuid;
	}
</script>

<Panel title="Terms accepted" faIcon="file-signature" class="mt-6">
	<svelte:fragment slot="titleRight">
		{#if acceptances !== undefined}
			<span class="text-sm text-default">{acceptances.length}</span>
		{/if}
	</svelte:fragment>

	{#if acceptances === undefined}
		<EmptyState
			compact
			title="This server does not report accepted terms."
			description="Nothing was recorded either way on this screen, so check each purchase directly."
		/>
	{:else if acceptances.length === 0}
		<EmptyState
			compact
			title="This customer has accepted no terms."
			description="Terms are set per product and per product family on the catalogue screen; a purchase made before one was attached carries none."
		/>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Term</TableHeadCell>
				<TableHeadCell>Version</TableHeadCell>
				<TableHeadCell>Answer</TableHeadCell>
				<TableHeadCell>Accepted</TableHeadCell>
				<TableHeadCell><span class="sr-only">Wording</span></TableHeadCell>
			</TableHead>
			<TableBody>
				{#each groups as group (group.key)}
					<!--
						The purchase's heading. A row rather than a table of its own, so every term
						this customer has agreed to still lines up on one set of columns.
					-->
					<TableBodyRow class="bg-depth">
						<TableBodyCell colspan={COLUMN_COUNT} class="dataTable !py-2">
							<div class="flex flex-wrap items-center gap-2">
								{#if group.href === null}
									<i class="fa-solid fa-file-circle-question text-xs opacity-60" aria-hidden="true" />
									<!-- Named rather than left blank: a heading with no words reads as a
									     bug, and this is a real state — the checkout session an
									     acceptance names is nullable, and a purchase can be gone. -->
									<span class="card-label !mb-0">{group.name}</span>
								{:else}
									<i class="fa-solid fa-receipt text-xs opacity-60" aria-hidden="true" />
									<!-- Straight to the purchase's own screen, which is where the rest
									     of the story is: its invoices, its options and its refunds. -->
									<RoutedLink to={group.href} class="card-label !mb-0 text-primary-500 hover:underline">
										{group.name}
									</RoutedLink>
								{/if}
								{#if group.purchasedAt !== null}
									<span class="text-xs text-default">
										bought {formatCalendarDate(group.purchasedAt)}
									</span>
								{/if}
								<span class="text-xs text-default">
									{group.acceptances.length}
									{group.acceptances.length === 1 ? 'term' : 'terms'}
								</span>
							</div>
						</TableBodyCell>
					</TableBodyRow>

					{#each group.acceptances as acceptance (acceptance.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{acceptance.title}</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div class="flex flex-wrap items-center gap-2">
									<Badge color="dark">v{acceptance.version}</Badge>
									{#if acceptanceVersionNote(acceptance) !== null}
										<!-- Not a fault, and worded so it does not read as one: the
										     customer agreed to what they were shown, and the catalogue
										     has moved since. -->
										<Badge color="yellow">
											{ACCEPTANCE_VERSION_COPY[acceptanceVersionNote(acceptance) ?? 'edited']}
										</Badge>
									{/if}
								</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- The question and its answer, together or not at all: the label is
								     snapshotted beside the value precisely so a term relabelled since
								     cannot restate an answer under a question nobody was asked. -->
								{acceptanceAnswer(acceptance) ?? '—'}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{formatFriendlyDate(acceptance.acceptedAt)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- A real button, so the keyboard reaches it. `aria-expanded`, because
								     what it discloses is a row further down the table. -->
								<button
									type="button"
									class="text-primary-500 hover:underline"
									aria-expanded={openUUID === acceptance.uuid}
									on:click={() => fnToggle(acceptance.uuid)}
								>
									{openUUID === acceptance.uuid ? 'Hide wording' : 'Wording'}
								</button>
							</TableBodyCell>
						</TableBodyRow>

						{#if openUUID === acceptance.uuid}
							<TableBodyRow>
								<TableBodyCell colspan={COLUMN_COUNT} class="dataTable">
									<!--
										The snapshot on the acceptance, never the live term: rendering
										today's wording under a date from last March would be evidence
										of something nobody agreed to, and would look exactly like
										evidence of something they did.

										`richtext`, because Tailwind's preflight strips the paragraph
										and list styling out of any HTML the app did not author — and a
										term of service rendered as one unbroken block is unreadable
										exactly where it matters.
									-->
									<div class="richtext text-sm">{@html acceptance.bodyHTML}</div>
								</TableBodyCell>
							</TableBodyRow>
						{/if}
					{/each}
				{/each}
			</TableBody>
		</Table>
	{/if}
</Panel>
